import {
  ComponentStyleProps,
  componentStylePropsOf,
} from "../../../lib/styles/props/component-style-props";
import React, { useMemo } from "react";
import {
  Badge,
  Box,
  ComponentMultiStyleConfig,
  Text,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import { hasValue } from "../../../lib/util/common-util";

export type CMCommentTitleProps = {
  /**
   * 未読・既読判定用フラグ
   * trueの場合: 未読状態
   */
  isUnread: boolean;

  /**
   * 未読状態の表示ラベル
   */
  messageUnread?: string;

  /**
   * 既読状態の表示ラベル
   */
  messageRead?: string;

  /**
   * コメントスレッドのタイトル
   */
  title?: string;
} & ComponentStyleProps;

/**
 * 未読・既読のバッジ表示と、タイトル表示する。
 */
export const CMCommentTitle = ({
  isUnread,
  messageUnread,
  messageRead,
  title,

  sx,
  ...rest
}: CMCommentTitleProps) => {
  const styles = useMultiStyleConfig("CMCommentTitleStyles");

  /**
   * バッジ表示ラベルの設定
   */
  const message = useMemo<string>(() => {
    const mesUnread = hasValue(messageUnread) ? messageUnread : "";
    const mesRead = hasValue(messageRead) ? messageRead : "";

    return isUnread ? mesUnread : mesRead;
  }, [isUnread, messageRead, messageUnread]);

  /**
   * バッジのスタイル設定
   */
  const appliedBadgeStyle = useMemo(
    () => ({
      ...styles.badgeBase,
      ...(isUnread ? styles.badgeUnread : styles.badgeRead),
    }),
    [isUnread, styles],
  );

  return (
    <Box sx={{ ...styles.container, ...sx }} {...rest}>
      <Badge sx={appliedBadgeStyle}>{message}</Badge>
      <Text sx={styles.title}>{title}</Text>
    </Box>
  );
};

export const CMCommentTitleStyles: ComponentMultiStyleConfig = {
  parts: ["container", "badgeUnread", "badgeUnRead", "badgeRead", "title"],
  baseStyle: {
    container: {
      display: "flex",
      flexDirection: "row",
      alignItems: "start",
    },
    badgeBase: {
      h: "18px",
      w: "fit-content",
      marginTop: "3px",
      marginRight: "8px",
      paddingRight: "10px",
      paddingLeft: "10px",
      textAlign: "center",
      borderRadius: "4px",
      fontSize: "12px",
      textTransform: "none",
    },
    badgeUnread: {
      color: "white",
      bgColor: "blue.500",
    },
    badgeRead: {
      color: "gray.900",
      bgColor: "gray.300",
    },
    title: {
      wordBreak: "break-word",
      fontSize: "16px",
      color: "gray.900",
    },
  },
};
